import {inject, Injectable} from './di';
import {Logger} from './logger';
import {ConfigService} from './config/config-service';
import {AppInitializeService} from './app-initialize-service';

const DEFAULT_TIMEOUT = 60_000; // 1 min
const DEFAULT_REBOOT_TIMEOUT = 10_000; // 10 seconds

@Injectable()
export class BootstrapService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);

  public readonly startupTime: Date = new Date();

  public async init() {
    try {
      this.logger.debug(`[BootstrapService] Bootstrap Service initialization started`);

      const config = this.configService.config;

      let timeout: number | undefined = config?.bootstrap?.timeout;
      if (timeout == null || !Number.isInteger(timeout)) {
        timeout = DEFAULT_TIMEOUT;
      }

      if (timeout <= 0) {
        this.logger.critical(
          `[BootstrapService] Application bootup timer suppressed by "config.bootstrap.timeout" config`,
        );
        // TODO remove legacy timeout
        window.system_start_timer = setTimeout(() => {});
        return Promise.resolve(true);
      }

      this.logger.debug('[BootstrapService] Application bootup timer started');

      // system_start_timer is legacy timeout that is canceled somewhere in legacy code
      window.system_start_timer = setTimeout(() => {
        this.logger.critical(
          `[BootstrapService] The application did not start successfully in ${timeout} milliseconds`,
        );
        const config = this.configService.config;

        // TODO config?.tv?.hacks?.notreloadontimeout is legacy setting
        let abortReboot: boolean | undefined =
          !!config?.tv?.hacks?.notreloadontimeout || !!config?.bootstrap?.doNotReboot;
        let rebootTimeout = config?.bootstrap?.rebootTimeout;

        if (rebootTimeout == null || !Number.isInteger(rebootTimeout)) {
          rebootTimeout = DEFAULT_REBOOT_TIMEOUT;
        }
        if (rebootTimeout <= 0 || !Number.isFinite(rebootTimeout)) {
          abortReboot = true;
        }

        if (abortReboot) {
          this.logger.log(`[BootstrapService] Application reboot is suppressed by config`);
        } else {
          this.logger.log(`[BootstrapService] Application will be rebooted in ${rebootTimeout} milliseconds`);
          setTimeout(() => {
            this.logger.log(`[BootstrapService] Rebooting application now...`);
            document.location.reload();
          }, rebootTimeout);
        }
      }, timeout);

      this.logger.debug(`[BootstrapService] Bootstrap Service initialization completed`);
      return Promise.resolve(true);
    } catch (err) {
      this.logger.critical(`[BootstrapService] Bootstrap Service initialization failed`, err);
      return Promise.resolve(false);
    }
  }

  // TODO call setAppLoaded function to report about successfully loaded app!
  /**
   * Mark application as fully loaded and ready to work
   */
  public setAppLoaded() {
    const loadTime = new Date();
    const bootupTime = Math.round((loadTime.getTime() - this.startupTime.getTime()) / 10) / 100;
    this.logger.log(`[App] Application loaded in ${bootupTime} seconds`);

    // TODO cancel start timer (after getting rig of system_start_timer)
  }
}
